/**
 * @file DemoMeetingPage.tsx
 * @brief `/app|company|admin/meetings/demo`: the built-in demo meeting in the
 *        real meeting page, read-only, clearly labelled "Demo".
 *
 * @details
 * Calm Home (DES 19-10-2026): a new user sees what a finished meeting looks
 * like (notes, decisions, to-dos, speakers, Ask) before recording anything.
 * Opened from Home's "Try a demo meeting" card, and from Help (the footer)
 * once the user has meetings. The data is in the bundle (demoMeeting.ts):
 * no request is made for it, it costs nothing and is never billed.
 *
 * Related: ./demoMeeting.ts, ./DemoAskPanel.tsx,
 *          ../company-meetings/components/MeetingDetailView.tsx (`demo`)
 *
 * @author DES
 * @date 20-10-2026
 */

import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import { readBackState } from "@/shared/navigation/backState";
import { MeetingDetailView } from "@/features/company-meetings/components/MeetingDetailView";
import { useBoardroomBasePath } from "@/features/employee-boardroom/useBoardroomBasePath";
import { DemoAskPanel } from "./DemoAskPanel";
import { demoMeeting } from "./demoMeeting";

export function DemoMeetingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = useBoardroomBasePath();
  const back = readBackState(location.state);
  const meeting = useMemo(() => demoMeeting(), []);
  const home = base === "/app" ? "/app" : `${base}/home`;

  return (
    <div className="flex w-full flex-col">
      {/* Always visible, so a sample never passes for a real record. */}
      <div
        role="note"
        className="mx-5 mt-4 flex items-center gap-2.5 rounded-2xl bg-brand/[0.06] px-4 py-2.5 text-sm text-app-text md:mx-8"
      >
        <FlaskConical className="h-4 w-4 shrink-0 text-brand" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="font-semibold">Demo</span>
          <span className="text-app-text-secondary">
            {" "}
            · A sample meeting with invented people. Read-only, free, and it never uses your
            minutes.
          </span>
        </span>
      </div>
      <MeetingDetailView
        meeting={meeting}
        scope="employee"
        layout="phone"
        onBack={() => navigate(back?.backTo ?? home)}
        backLabel={back?.backLabel ?? "Home"}
        demo={{ askPanel: <DemoAskPanel /> }}
      />
    </div>
  );
}

export default DemoMeetingPage;
